/**
 * Случайный UUID для request ID, ключей идемпотентности и `client_message_id`.
 *
 * `crypto.randomUUID` существует только в secure context: по HTTPS, на
 * `localhost` и на `127.0.0.1`. Дев-стенд, открытый по HTTP с любого другого
 * адреса (`http://192.168.1.10:8080` с телефона, `http://host.docker.internal:8080`
 * из контейнера Playwright), secure context не является — там метода нет, и
 * прямой вызов бросает `TypeError`. В `apiFetch` он выполняется до `fetch`,
 * поэтому запрос не уходит вовсе: приложение молча стоит на скелетонах, а в
 * сетевой панели пусто. Именно так были сняты эталоны визуальной регрессии.
 *
 * `crypto.getRandomValues` доступен всегда — ограничение secure context
 * распространяется на `crypto.subtle` и `randomUUID`, но не на него. Поэтому
 * запасной путь остаётся криптографически стойким, а не уходит в `Math.random`.
 *
 * Версия 4, как и у `crypto.randomUUID`: UUIDv7 конвенции хранения (CLAUDE.md §5)
 * относятся к первичным ключам, которые генерирует backend. Здесь UUID нужен
 * только как непредсказуемый идентификатор запроса.
 */
export function randomUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // Версия (4) и вариант (RFC 4122) — те же биты, что расставляет `randomUUID`.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}
